import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronRight, ChevronLeft, X } from 'lucide-react';
import Header from '../../components/ui/Header';
import { supabase } from '../../lib/supabase';
import LoadingPage from './LoadingPage';
import ResultsDisplay from './ResultsDisplay';
import AIUniversityDetailsModal from './AIUniversityDetailsModal';
import { saveResults, fetchResults, deleteResults } from '../portfolio-builder/lib/documentService';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config.js';

// Mock recommendations generator
const generateMockRecommendations = (responses, selectedPath) => {
  const mockUniversities = [
    {
      id: 1,
      name: 'Massachusetts Institute of Technology',
      country: 'United States',
      matching_score: 92,
      details: {
        overview: 'World-leading institute in tech and innovation with hands-on learning.',
        location: 'Cambridge, MA',
        ranking: 'Top 5',
        tuition: '$53,790 per year',
        notable_strengths: ['AI/ML leadership', 'Entrepreneurship', 'Cutting-edge research']
      },
      why_for_you: ['Strong alignment with tech ambitions', 'Robust research opportunities', 'Collaborative culture'],
      program_meant_for_you: {
        name: 'Computer Science & Engineering',
        degree: 'B.S./M.S.',
        description: 'Deep foundations with practical labs and research.',
        specializations: ['AI/ML', 'Robotics', 'Systems']
      },
      what_you_will_learn: ['Algorithmic thinking', 'System design', 'Applied machine learning'],
      eligibility: {
        met_requirements: ['Strong STEM background'],
        missing_requirements: ['SAT Subject Tests (optional)'],
        suggestions: 'Build portfolio projects showcasing ML experience.'
      }
    },
    {
      id: 2,
      name: 'Stanford University',
      country: 'United States',
      matching_score: 88,
      details: {
        overview: 'Entrepreneurial ecosystem with interdisciplinary excellence.',
        location: 'Stanford, CA',
        ranking: 'Top 5',
        tuition: '$56,169 per year',
        notable_strengths: ['Startups', 'Industry ties', 'Innovation labs']
      },
      why_for_you: ['Proximity to Silicon Valley', 'Strong mentorship', 'Flexible curriculum'],
      program_meant_for_you: {
        name: 'Computer Science',
        degree: 'B.S./M.S.',
        description: 'Broad foundations and modern specialization tracks.',
        specializations: ['AI', 'HCI', 'Systems']
      },
      what_you_will_learn: ['Software engineering', 'Human-centered design', 'AI fundamentals'],
      eligibility: {
        met_requirements: ['Competitive academics'],
        missing_requirements: ['Additional leadership evidence'],
        suggestions: 'Lead a tech initiative or open-source project.'
      }
    },
    {
      id: 3,
      name: 'Harvard University',
      country: 'United States',
      matching_score: 85,
      details: {
        overview: 'Historic university with broad academic excellence.',
        location: 'Cambridge, MA',
        ranking: 'Top 10',
        tuition: '$54,269 per year',
        notable_strengths: ['Faculty access', 'Alumni network', 'Interdisciplinary study']
      },
      why_for_you: ['Well-rounded curriculum', 'Research depth', 'Global brand'],
      program_meant_for_you: {
        name: 'Computer Science',
        degree: 'B.A.',
        description: 'Strong theory with practical applications.',
        specializations: ['Systems', 'Data', 'Security']
      },
      what_you_will_learn: ['Data structures', 'Security practices', 'Collaborative projects'],
      eligibility: {
        met_requirements: ['Academic excellence'],
        missing_requirements: ['More community impact'],
        suggestions: 'Volunteer as a mentor or organizer in tech communities.'
      }
    }
  ];

  const mockFields = [
    {
      id: 1,
      name: 'Computer Science & AI',
      growth: '15% annually',
      avgSalary: '$125,000+',
      topCompanies: ['Google', 'Meta', 'Apple', 'Microsoft'],
      skills: ['Programming', 'Problem-solving', 'Machine Learning', 'System Design'],
      career_paths: ['Software Engineer', 'AI Researcher', 'Data Scientist', 'Tech Lead']
    },
    {
      id: 2,
      name: 'Business & Entrepreneurship',
      growth: '8% annually',
      avgSalary: '$110,000+',
      topCompanies: ['McKinsey', 'Goldman Sachs', 'Deloitte', 'JPMorgan'],
      skills: ['Leadership', 'Strategic Planning', 'Finance', 'Communication'],
      career_paths: ['Management Consultant', 'Investment Banker', 'Entrepreneur', 'Product Manager']
    }
  ];

  if (selectedPath === 'university') {
    return {
      type: 'university',
      universities: mockUniversities,
      insights: {
        summary: 'Based on your preferences, we found top universities matching your profile.',
        nextSteps: ['Research campus life', 'Connect with current students', 'Start application prep']
      }
    };
  } else if (selectedPath === 'field') {
    return {
      type: 'field',
      recommendations: mockFields,
      insights: {
        summary: 'Here are the top fields aligned with your interests and skills.',
        nextSteps: ['Explore programs offering these fields', 'Connect with professionals', 'Build relevant skills']
      }
    };
  } else {
    return {
      type: 'unsure',
      universities: mockUniversities,
      insights: {
        summary: 'Based on your interests, we recommend exploring these universities and fields.',
        nextSteps: ['Take career assessment', 'Schedule campus tours', 'Speak with advisors']
      }
    };
  }
};

const UniGuideAI = () => {
  const [stage, setStage] = useState('entry'); // 'entry', 'wizard', 'loading', 'results'
  const [selectedPath, setSelectedPath] = useState(null); // 'university', 'field', 'unsure'
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [aiResults, setAIResults] = useState(null);
  const [user, setUser] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [selectedUniDetail, setSelectedUniDetail] = useState(null);

  // Check for existing results on component mount
  useEffect(() => {
    const checkExistingResults = async () => {
      try {
        const {
          data: { user: authUser },
        } = await supabase.auth.getUser();

        if (authUser) {
          setUser(authUser);
          const existingResults = await fetchResults(authUser.id);
          if (existingResults) {
            setSelectedPath(existingResults.selectedPath);
            setResponses(existingResults.responses);
            setAIResults(existingResults.aiResults);
            setStage('results');
          }
        }
      } catch (error) {
        console.error('Error checking existing results:', error);
      }
    };

    checkExistingResults();
  }, []);

  const handleSelectPath = (path) => {
    setSelectedPath(path);
    setCurrentStep(0);
    setResponses({});
    setStage('wizard');
  };

  const handleBackToEntry = () => {
    setStage('entry');
    setSelectedPath(null);
    setCurrentStep(0);
    setResponses({});
    setAIResults(null);
  };

  const handleEditAnswers = () => {
    // Clear from database
    if (user && aiResults) {
      deleteResults(user.id).catch(err => console.error('Error deleting results:', err));
    }
    setStage('wizard');
    setAIResults(null);
    setCurrentStep(0);
  };

  const handleResetConfirm = async () => {
    try {
      if (user) {
        await deleteResults(user.id);
      }
      setShowResetConfirm(false);
      handleBackToEntry();
    } catch (error) {
      console.error('Error resetting:', error);
    }
  };

  // Define flows for each path
  const flows = {
    university: [
      {
        id: 'university_name',
        type: 'text',
        question: 'What university are you interested in?',
        placeholder: 'e.g., University of Oxford, MIT, Stanford',
        description: 'Enter the name of the university you\'re considering',
      },
      {
        id: 'degree_level',
        type: 'select',
        question: 'What degree level are you targeting?',
        options: ['Bachelor\'s', 'Master\'s', 'PhD', 'Diploma', 'Certificate Program'],
        description: 'This helps us provide relevant information',
      },
      {
        id: 'field_of_study',
        type: 'text',
        question: 'What field are you interested in studying?',
        placeholder: 'e.g., Computer Science, Medicine, Business Administration',
        description: 'Tell us your area of interest',
      },
      {
        id: 'specific_program',
        type: 'text',
        question: 'Do you have a specific program in mind?',
        placeholder: 'e.g., MSc in Artificial Intelligence, MBA, Biomedical Engineering',
        description: 'Optional - Leave blank if unsure',
      },
      {
        id: 'current_education',
        type: 'select',
        question: 'What is your current education level?',
        options: [
          'High School Student',
          'High School Graduate',
          'Bachelor\'s Student',
          'Bachelor\'s Graduate',
          'Master\'s Student',
          'Master\'s Graduate',
          'Working Professional',
        ],
        description: 'Helps us understand your academic background',
      },
      {
        id: 'academic_performance',
        type: 'select',
        question: 'How would you describe your academic performance?',
        options: [
          'Excellent (Top 10%)',
          'Very Good (Top 25%)',
          'Good (Top 50%)',
          'Average',
          'Prefer not to say',
        ],
        description: 'This helps us assess admission chances',
      },
      {
        id: 'gpa_score',
        type: 'text',
        question: 'What is your GPA or equivalent score?',
        placeholder: 'e.g., 3.8/4.0, 85%, First Class',
        description: 'Optional - Your current or most recent academic score',
      },
      {
        id: 'entrance_exams',
        type: 'select',
        question: 'Have you taken or plan to take standardized tests?',
        options: [
          'SAT/ACT (taken)',
          'SAT/ACT (planning)',
          'GRE (taken)',
          'GRE (planning)',
          'GMAT (taken)',
          'GMAT (planning)',
          'IELTS/TOEFL (taken)',
          'IELTS/TOEFL (planning)',
          'Not required/Not planning',
        ],
        description: 'Select all that apply to your application',
      },
      {
        id: 'why_this_university',
        type: 'select',
        question: 'What attracts you most to this university?',
        options: [
          'Academic reputation',
          'Specific programs',
          'Research opportunities',
          'Location',
          'Career prospects',
          'Campus culture',
          'Alumni network',
          'Financial aid',
        ],
        description: 'This helps us highlight relevant aspects',
      },
      {
        id: 'application_timeline',
        type: 'select',
        question: 'When do you plan to apply?',
        options: [
          'This semester',
          'Next semester',
          'This year',
          'Next year',
          'In 2+ years',
          'Just researching',
        ],
        description: 'We\'ll provide timeline-appropriate guidance',
      },
      {
        id: 'funding_needs',
        type: 'select',
        question: 'What are your funding requirements?',
        options: [
          'Need full scholarship',
          'Need partial scholarship',
          'Interested in student loans',
          'Self-funded',
          'Employer-sponsored',
          'Not sure yet',
        ],
        description: 'Helps us show relevant financial aid options',
      },
      {
        id: 'career_goals',
        type: 'text',
        question: 'What are your career goals after graduation?',
        placeholder: 'e.g., Software Engineer at tech company, Medical Researcher, Start own business',
        description: 'This helps us align recommendations with your aspirations',
      },
    ],
    field: [
      {
        id: 'field_of_interest',
        type: 'select',
        question: 'What field are you interested in?',
        options: [
          'Computer Science & Tech',
          'Engineering (Mechanical, Electrical, etc.)',
          'Business & Economics',
          'Medicine & Health Sciences',
          'Law & Legal Studies',
          'Arts & Humanities',
          'Natural Sciences (Physics, Chemistry, Biology)',
          'Social Sciences (Psychology, Sociology)',
          'Mathematics & Statistics',
          'Architecture & Design',
          'Education',
          'Environmental Studies',
        ],
        description: 'Choose your primary area of interest',
      },
      {
        id: 'specific_specialization',
        type: 'text',
        question: 'Any specific specialization within this field?',
        placeholder: 'e.g., Machine Learning, Cardiology, International Business',
        description: 'Optional - Be as specific as you like',
      },
      {
        id: 'degree_level',
        type: 'select',
        question: 'What degree level are you targeting?',
        options: ['Bachelor\'s', 'Master\'s', 'PhD', 'Professional Degree', 'Not sure yet'],
        description: 'This helps narrow down suitable programs',
      },
      {
        id: 'preferred_regions',
        type: 'select',
        question: 'Which region interests you most?',
        options: [
          'United States',
          'United Kingdom',
          'Western Europe',
          'Eastern Europe',
          'Canada',
          'Australia/New Zealand',
          'Asia (East Asia)',
          'Asia (South/Southeast Asia)',
          'Middle East',
          'No preference',
        ],
        description: 'We\'ll find universities in your preferred region',
      },
      {
        id: 'budget_range',
        type: 'select',
        question: 'What\'s your total budget per year (tuition + living)?',
        options: [
          'Under $10,000/year',
          '$10,000 - $25,000/year',
          '$25,000 - $50,000/year',
          '$50,000 - $75,000/year',
          'Over $75,000/year',
          'Budget flexible/Seeking scholarships',
        ],
        description: 'This helps us find universities that fit your budget',
      },
      {
        id: 'scholarship_requirement',
        type: 'select',
        question: 'How important is scholarship/financial aid?',
        options: [
          'Essential - Need 100% funding',
          'Very important - Need 50%+ funding',
          'Helpful but not required',
          'Not needed',
        ],
        description: 'We\'ll prioritize universities with strong aid programs',
      },
      {
        id: 'language_proficiency',
        type: 'select',
        question: 'What is your English proficiency level?',
        options: [
          'Native speaker',
          'Fluent (C1-C2)',
          'Advanced (B2)',
          'Intermediate (B1)',
          'Need to improve',
          'Plan to take English program first',
        ],
        description: 'Some programs may require language tests',
      },
      {
        id: 'study_mode',
        type: 'select',
        question: 'What study mode do you prefer?',
        options: [
          'Full-time on-campus',
          'Part-time on-campus',
          'Online/Distance learning',
          'Hybrid (mix of online and on-campus)',
          'No preference',
        ],
        description: 'This affects university and program options',
      },
      {
        id: 'university_size',
        type: 'select',
        question: 'Do you prefer a specific university size?',
        options: [
          'Large (15,000+ students)',
          'Medium (5,000-15,000 students)',
          'Small (Under 5,000 students)',
          'No preference',
        ],
        description: 'Class sizes and campus feel vary with university size',
      },
      {
        id: 'location_type',
        type: 'select',
        question: 'What type of location do you prefer?',
        options: [
          'Major city (urban)',
          'Mid-size city (suburban)',
          'College town (small city)',
          'Rural/Countryside',
          'No preference',
        ],
        description: 'Location affects lifestyle and opportunities',
      },
      {
        id: 'priority_factors',
        type: 'select',
        question: 'What matters most to you in a university?',
        options: [
          'Academic reputation/rankings',
          'Research opportunities',
          'Industry connections/internships',
          'Campus facilities',
          'Student support services',
          'Diversity and international community',
          'Affordability',
          'Post-graduation employment rate',
        ],
        description: 'Helps us personalize recommendations',
      },
      {
        id: 'extracurriculars',
        type: 'text',
        question: 'What are your key extracurricular activities or achievements?',
        placeholder: 'e.g., Student council, volunteering, sports, competitions, projects',
        description: 'Optional - This strengthens your profile',
      },
    ],
    unsure: [
      {
        id: 'career_interest',
        type: 'buttons',
        question: 'What excites you the most?',
        options: [
          { label: '💻 Technology & Innovation', value: 'tech' },
          { label: '🏥 Health & Medicine', value: 'health' },
          { label: '🏢 Business & Finance', value: 'business' },
          { label: '🔬 Research & Science', value: 'science' },
          { label: '✍️ Arts & Culture', value: 'arts' },
          { label: '🌍 Social Impact', value: 'social' },
        ],
        description: 'Select the area that resonates most with you',
      },
      {
        id: 'strengths',
        type: 'select',
        question: 'What are you naturally good at?',
        options: [
          'Problem-solving and logic',
          'Creative thinking',
          'Working with people',
          'Analysis and research',
          'Communication and writing',
          'Hands-on practical work',
          'Leadership and organization',
        ],
        description: 'Understanding your strengths helps guide recommendations',
      },
      {
        id: 'interests_hobbies',
        type: 'text',
        question: 'What are your main hobbies or interests?',
        placeholder: 'e.g., coding, reading, sports, music, volunteering, traveling',
        description: 'Your interests can point to suitable fields of study',
      },
      {
        id: 'favorite_subjects',
        type: 'text',
        question: 'What were your favorite subjects in school?',
        placeholder: 'e.g., Mathematics, History, Biology, Literature',
        description: 'This helps identify fields you might enjoy',
      },
      {
        id: 'career_vision',
        type: 'select',
        question: 'What kind of career do you envision?',
        options: [
          'Working for a big corporation',
          'Starting my own business',
          'Research and academia',
          'Non-profit/social sector',
          'Government/public service',
          'Freelance/creative work',
          'Healthcare professional',
          'Not sure yet',
        ],
        description: 'This helps align university programs with career paths',
      },
      {
        id: 'work_environment',
        type: 'select',
        question: 'What work environment appeals to you?',
        options: [
          'Office/corporate setting',
          'Laboratory/research facility',
          'Hospital/clinical setting',
          'Outdoors/field work',
          'Remote/work from anywhere',
          'Studio/creative space',
          'Mix of different environments',
        ],
        description: 'Different fields offer different work settings',
      },
      {
        id: 'study_style',
        type: 'buttons',
        question: 'How do you prefer to study?',
        options: [
          { label: '🏙️ Big Campus', value: 'big' },
          { label: '🏘️ Small University', value: 'small' },
          { label: '🌍 International', value: 'international' },
          { label: '💼 Industry-Focused', value: 'industry' },
          { label: '🎓 Research-Oriented', value: 'research' },
          { label: '🎨 Creative & Flexible', value: 'creative' },
        ],
        description: 'University culture varies widely',
      },
      {
        id: 'learning_preference',
        type: 'select',
        question: 'How do you learn best?',
        options: [
          'Hands-on practical experience',
          'Theoretical and conceptual',
          'Mix of theory and practice',
          'Group projects and collaboration',
          'Independent study',
          'Visual and creative methods',
        ],
        description: 'Different programs emphasize different learning styles',
      },
      {
        id: 'education_level',
        type: 'select',
        question: 'What is your current education level?',
        options: [
          'High School Student',
          'High School Graduate',
          'Some College/University',
          'Bachelor\'s Degree',
          'Working Professional',
          'Career Change',
        ],
        description: 'Helps us suggest appropriate degree levels',
      },
      {
        id: 'budget_awareness',
        type: 'select',
        question: 'What\'s your budget situation?',
        options: [
          'Very limited - Need full scholarship',
          'Moderate - Need partial funding',
          'Flexible - Can afford most programs',
          'Not a constraint',
          'Haven\'t thought about it yet',
        ],
        description: 'Budget affects which universities we recommend',
      },
      {
        id: 'location_openness',
        type: 'select',
        question: 'How do you feel about studying abroad?',
        options: [
          'Very excited - Prefer international',
          'Open to it',
          'Prefer staying in home country',
          'Only domestic options',
          'Undecided',
        ],
        description: 'Opens up different opportunities',
      },
      {
        id: 'help_needed',
        type: 'select',
        question: 'What type of help would you find most useful?',
        options: [
          'Discovering what to study',
          'Finding the right universities',
          'Understanding admission requirements',
          'Scholarship and funding guidance',
          'Application process support',
          'Career path exploration',
          'All of the above',
        ],
        description: 'We\'ll tailor our guidance to your needs',
      },
    ],
  };

  const flowSteps = flows[selectedPath] || [];
  const totalSteps = flowSteps.length;
  const currentQuestion = flowSteps[currentStep];
  const progressPercent = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;

  const MULTI_FIELDS = new Set(['preferred_regions', 'priority_factors', 'field_of_interest']);

  const handleNext = (answer) => {
    setResponses({
      ...responses,
      [currentQuestion.id]: answer,
    });

    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    try {
      const {
        data: { user: authUser },
        error: userError
      } = await supabase.auth.getUser();

      if (!authUser || userError) {
        console.error('User not authenticated');
        alert('Please sign in to save your onboarding responses');
        return;
      }

      setUser(authUser);
      setStage('loading');

      // Call backend API to generate recommendations
      try {
        // Fetch portfolio documents if any
        const { data: portfolioData } = await supabase
          .from('portfolio_documents')
          .select('*')
          .eq('user_id', authUser.id);

        // Fetch personal info if available
        const { data: personalData } = await supabase
          .from('personal_info')
          .select('*')
          .eq('user_id', authUser.id)
          .single();

        // Transform data for API
        const portfolioItems = portfolioData?.map(doc => ({
          category: doc.category,
          title: doc.title,
          description: doc.description,
        })) || [];

        const personalInfo = personalData ? {
          firstName: personalData.first_name,
          lastName: personalData.last_name,
          email: personalData.email,
          grade: personalData.grade,
          country: personalData.country,
        } : {};

        // Call the backend API with fallback to mock data
        let recommendationsData;
        try {
          const apiResponse = await axios.post(API_ENDPOINTS.generateRecommendations, {
            preferences: responses,
            portfolioItems,
            personalInfo,
          });

          if (apiResponse.data.success) {
            recommendationsData = apiResponse.data.data;
          } else {
            throw new Error(apiResponse.data.error || 'Failed to generate recommendations');
          }
        } catch (apiError) {
          console.warn('Backend API not available, using mock recommendations:', apiError.message);
          // Generate mock recommendations locally
          recommendationsData = generateMockRecommendations(responses, selectedPath);
        }

        // Save results to database
        await saveResults(authUser.id, selectedPath, responses, recommendationsData);
        setAIResults(recommendationsData);
        setStage('results');
      } catch (apiError) {
        console.error('API Error:', apiError);
        alert(`Failed to generate recommendations: ${apiError.message}`);
        setStage('wizard');
      }
    } catch (error) {
      console.error('Error in handleComplete:', error);
      alert('An error occurred. Please try again.');
      setStage('wizard');
    }
  };

  const pathCards = [
    {
      id: 'university',
      title: 'I already have a university in mind',
      description: 'Get personalized guidance and compare your choice',
      icon: '🎓',
    },
    {
      id: 'field',
      title: 'I know the field, not the university',
      description: 'Find universities that match your interests',
      icon: '🔍',
    },
    {
      id: 'unsure',
      title: "I'm not sure yet",
      description: 'Let AI help you explore and discover',
      icon: '✨',
    },
  ];

  return (
    <>
      <Helmet>
        <title>UniGuide AI - UniSteps</title>
        <meta name="description" content="UniGuide AI by UniSteps – AI-powered university discovery and guidance." />
      </Helmet>

      <Header />

      <div className="min-h-screen app-shell">
        {stage === 'loading' && <LoadingPage onCancel={() => setStage('wizard')} />}

        {stage === 'results' && aiResults && (
          <>
            {showResetConfirm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">Reset All Data?</h3>
                    <button
                      onClick={() => setShowResetConfirm(false)}
                      className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 mb-6">
                    This will delete your onboarding responses and recommendations. You'll need to start the form again.
                  </p>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setShowResetConfirm(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleResetConfirm}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
                    >
                      Delete All
                    </button>
                  </div>
                </div>
              </div>
            )}
            <ResultsDisplay
              results={{ ...aiResults, onViewDetails: (uni) => setSelectedUniDetail(uni) }}
              onReset={() => setShowResetConfirm(true)}
              onEditAnswers={handleEditAnswers}
            />

            {selectedUniDetail && (
              <AIUniversityDetailsModal
                university={selectedUniDetail}
                onClose={() => setSelectedUniDetail(null)}
                userProfile={{ userId: user?.id }}
                preferences={responses}
              />
            )}
          </>
        )}

        {stage === 'entry' && (
          // ENTRY SCREEN
          <div className="min-h-screen bg-gradient-to-b from-background via-background to-background flex flex-col items-center justify-center px-4 py-12 pt-20 relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-10 left-[-10%] w-80 h-80 bg-primary/10 rounded-full blur-3xl opacity-50 floating"></div>
              <div className="absolute bottom-10 right-[-8%] w-80 h-80 bg-accent/10 rounded-full blur-3xl opacity-50 floating" style={{ animationDelay: '1.5s' }}></div>
            </div>

            <div className="relative z-10 w-full max-w-6xl">
              {/* Header Section */}
              <div className="text-center mb-16">
                <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-4 tracking-tight headline-serif">
                  Find your <span className="hero-accent">university path</span>
                </h1>
                <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                  UniGuide AI uses intelligent algorithms to help you discover and choose the perfect university. 
                  Answer a few questions and get personalized recommendations tailored to your goals.
                </p>
              </div>

              {/* Cards Grid */}
              <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                {pathCards.map((path, index) => (
                  <button
                    key={path.id}
                    onClick={() => handleSelectPath(path.id)}
                    className="group relative h-96 overflow-hidden rounded-2xl card-gradient border border-border hover:border-primary/60 transition-all duration-300 cursor-pointer lifted"
                    style={{
                      animation: `slideInUp 0.6s ease-out ${index * 0.1}s backwards`,
                    }}
                  >
                    {/* Hover gradient background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-accent/0 group-hover:from-primary/8 group-hover:to-accent/6 transition-all duration-300"></div>

                    {/* Gold accent line on hover */}
                    <div className="absolute top-0 left-0 w-0 h-1 bg-gradient-to-r from-primary to-accent group-hover:w-full transition-all duration-500"></div>

                    {/* Content */}
                    <div className="relative p-8 h-full flex flex-col justify-between">
                      {/* Icon */}
                      <div className="text-6xl mb-4 transform group-hover:scale-110 transition-transform duration-300">
                        {path.icon}
                      </div>

                      {/* Text */}
                      <div className="flex-1 flex flex-col justify-end">
                        <h3 className="text-xl font-semibold text-foreground mb-3 text-left group-hover:text-primary transition-colors duration-300">
                          {path.title}
                        </h3>
                        <p className="text-muted-foreground text-sm leading-relaxed mb-4 text-left">
                          {path.description}
                        </p>

                        {/* Arrow indicator */}
                        <div className="flex items-center text-primary opacity-0 group-hover:opacity-100 transform group-hover:translate-x-2 transition-all duration-300">
                          <span className="text-sm font-medium mr-2">Get started</span>
                          <ChevronRight size={18} />
                        </div>
                      </div>

                      {/* Corner accent on hover */}
                      <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tl from-amber-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-tl-full"></div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Footer text */}
              <div className="text-center mt-16 text-muted-foreground text-sm">
                <p>✓ AI-powered analysis • ✓ Takes 2-3 minutes • ✓ Personalized recommendations</p>
              </div>
            </div>

            {/* CSS Animations */}
            <style>{`
              @keyframes slideInUp {
                from {
                  opacity: 0;
                  transform: translateY(30px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            `}</style>
          </div>
        )}

        {stage === 'wizard' && (
          // WIZARD SCREEN
          <div className="min-h-screen bg-gradient-to-b from-background via-background to-background flex flex-col items-center justify-center px-4 py-12 pt-20 relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-1/3 left-1/2 w-96 h-96 bg-primary/8 rounded-full blur-3xl opacity-45 transform -translate-x-1/2"></div>
            </div>

            <div className="relative z-10 w-full max-w-2xl">
              {/* Header with back button */}
              <div className="flex items-center justify-between mb-12">
                <button
                  onClick={handleBackToEntry}
                  className="flex items-center text-muted-foreground hover:text-foreground transition-colors duration-300 group"
                >
                  <ChevronLeft size={20} className="mr-1 group-hover:-translate-x-1 transition-transform" />
                  <span className="text-sm">Back</span>
                </button>
                <div className="text-muted-foreground text-sm">
                  Step {currentStep + 1} of {totalSteps}
                </div>
              </div>

              {/* Progress bar */}
              <div className="mb-8">
                <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  ></div>
                </div>
              </div>

              {/* Question Container */}
              <div className="card-gradient border border-border rounded-2xl p-8 md:p-12 shadow-2xl">
                <div className="mb-8">
                  <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3 headline-serif">
                    {currentQuestion?.question}
                  </h2>
                  {currentQuestion?.description && (
                    <p className="text-muted-foreground text-lg">
                      {currentQuestion.description}
                    </p>
                  )}
                </div>

                {/* Question content based on type */}
                <div className="mb-8">
                  {currentQuestion?.type === 'text' && (
                    <input
                      type="text"
                      placeholder={currentQuestion.placeholder}
                      defaultValue={responses[currentQuestion.id] || ''}
                      onChange={(e) => {
                        setResponses({
                          ...responses,
                          [currentQuestion.id]: e.target.value,
                        });
                      }}
                      className="w-full px-6 py-4 bg-secondary/70 border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300 text-lg"
                    />
                  )}

                  {currentQuestion?.type === 'select' && (
                    <div>
                      {MULTI_FIELDS.has(currentQuestion.id) && (
                        <div className="flex justify-end mb-3">
                          <span className="text-sm text-muted-foreground">
                            Selected: {Array.isArray(responses[currentQuestion.id]) ? responses[currentQuestion.id].length : 0}
                          </span>
                        </div>
                      )}
                      <div className={`${MULTI_FIELDS.has(currentQuestion.id) ? 'grid grid-cols-1 md:grid-cols-2 gap-3' : 'space-y-3'}`}>
                      {currentQuestion.options.map((option) => {
                        const isMulti = MULTI_FIELDS.has(currentQuestion.id);
                        const currentValue = responses[currentQuestion.id];
                        const isSelected = isMulti
                          ? Array.isArray(currentValue) && currentValue.includes(option)
                          : currentValue === option;

                        const handleSelect = () => {
                          if (!isMulti) {
                            setResponses({
                              ...responses,
                              [currentQuestion.id]: option,
                            });
                            return;
                          }
                          const existing = Array.isArray(currentValue) ? currentValue : [];
                          const next = isSelected
                            ? existing.filter((o) => o !== option)
                            : [...existing, option];
                          setResponses({
                            ...responses,
                            [currentQuestion.id]: next,
                          });
                        };

                        if (isMulti) {
                          return (
                            <label
                              key={option}
                              className={`group flex flex-col items-center gap-3 w-full px-6 py-6 rounded-xl border-2 transition-all duration-300 text-center font-medium ${
                                isSelected
                                  ? 'border-primary bg-gray-100 dark:bg-gray-800 text-foreground ring-2 ring-primary/30'
                                  : 'border-border bg-secondary/70 text-foreground hover:border-primary/50 hover:bg-secondary/60'
                              } hover:shadow-md`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={handleSelect}
                                className="h-6 w-6 accent-amber-500 border-border rounded-md"
                                aria-label={option}
                              />
                              <span className="whitespace-normal break-words leading-snug">{option}</span>
                            </label>
                          );
                        }
                        
                        return (
                          <button
                            key={option}
                            onClick={handleSelect}
                            className={`w-full px-6 py-4 rounded-xl border-2 transition-all duration-300 text-left font-medium ${
                              isSelected
                                ? 'border-primary bg-gray-100 dark:bg-gray-800 text-foreground'
                                : 'border-border bg-secondary/70 text-foreground hover:border-primary/50 hover:bg-secondary/60'
                            }`}
                          >
                            {option}
                          </button>
                        );
                      })}
                      </div>
                    </div>
                  )}

                  {currentQuestion?.type === 'buttons' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {currentQuestion.options.map((option) => (
                        <button
                          key={option.value}
                          onClick={() =>
                            setResponses({
                              ...responses,
                              [currentQuestion.id]: option.value,
                            })
                          }
                          className={`px-6 py-6 rounded-xl border-2 transition-all duration-300 font-medium text-center group ${
                            responses[currentQuestion.id] === option.value
                              ? 'border-primary bg-gray-100 dark:bg-gray-800 text-foreground'
                              : 'border-border bg-secondary/70 text-foreground hover:border-primary/50 hover:bg-secondary/60'
                          }`}
                        >
                          <div className="text-2xl mb-2">{option.label.split(' ')[0]}</div>
                          <div className="text-sm">{option.label.split(' ').slice(1).join(' ')}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Navigation buttons */}
                <div className="flex gap-4 pt-8 border-t border-gray-700">
                  <button
                    onClick={handlePrevious}
                    disabled={currentStep === 0}
                    className="flex-1 px-6 py-3 bg-secondary/70 border border-border rounded-xl text-foreground font-medium hover:bg-secondary/60 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <ChevronLeft size={18} />
                    <span>Back</span>
                  </button>
                  <button
                    onClick={() => handleNext(responses[currentQuestion?.id])}
                    disabled={(() => {
                      const val = responses[currentQuestion?.id];
                      if (MULTI_FIELDS.has(currentQuestion?.id)) {
                        return !Array.isArray(val) || val.length === 0;
                      }
                      return !val;
                    })()}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl text-black font-semibold hover:from-amber-400 hover:to-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 group"
                  >
                    <span>{currentStep === totalSteps - 1 ? 'Complete' : 'Next'}</span>
                    {currentStep < totalSteps - 1 && (
                      <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    )}
                  </button>
                </div>
              </div>

              {/* Help text */}
              <div className="text-center mt-8 text-gray-500 text-sm">
                <p>Your responses help UniGuide AI personalize your university recommendations</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UniGuideAI;
